/**
 * TILL HARDWARE PROFILES — what differs between the physical units, declared
 * once instead of remembered.
 *
 * This is the seed of `AGENTIC_POS/device-profiles.json`. It lives here as a
 * COPY for now, the same way `device-models.ts` copies the payment-model table,
 * so the panel can ask for the right connection details today without waiting
 * on a published registry. When the JSON lands, this becomes its reader and the
 * installer script reads the SAME file (it already parses build-targets.json
 * with python3), so a till's quirks stop living in a `case "$DEV_MODEL"` in
 * bash, in comments, and in somebody's head.
 *
 * `match` is `ro.product.model`, which is what the device reports about itself
 * and what install-device.sh already branches on.
 */

export type TransportKind = 'adb-tcp' | 'wireless-debugging';

export interface DeviceProfile {
  id: string;
  displayName: string;
  /** `ro.product.model` as the device reports it. */
  match: string;
  /**
   * The builds (build-target ids) this machine installs. The device editor
   * offers only the profiles of the build it has chosen — a SUNMI profile never
   * appears under the TC-Touch build, and the reverse.
   */
  buildTargets: readonly string[];
  transport: {
    kind: TransportKind;
    /** Default TCP port when the address carries none. */
    defaultPort?: number;
    /** A one-time 6-digit handshake on a SECOND random port, done at the unit. */
    needsPairing: boolean;
    /** Whether a plain USB cable is a route at all. */
    usb: boolean;
  };
  /** Can a boot animation be pushed over adb, or does it need a vendor image? */
  bootBrandingOverAdb: boolean;
  /** Shown when bootBrandingOverAdb is false, so "no" comes with a reason. */
  notes?: string;
  /** What an installer should expect to find on this unit's USB bus. */
  peripherals: PeripheralExpectation[];
}

/**
 * ONE PERIPHERAL THIS HARDWARE IS EXPECTED TO HAVE, AND HOW IT ATTACHES.
 *
 * The install panel already lists the USB ids it SAW. That answers "what is on
 * the bus" and not the question an installer actually has, which is "is what I
 * am looking at what this machine is supposed to have". A K10 with no printer
 * id on the bus is fine — its printer is internal and never enumerates as a
 * separate box. An A10 with no printer id is a unit going out the door without
 * the hub plugged in. Same absence, opposite verdicts, and nothing on the
 * screen could tell them apart.
 *
 * `usbIds` are the ids the installer's own probe reports, so expected and seen
 * are comparable without a translation table in between.
 */
export interface PeripheralExpectation {
  kind: 'printer' | 'scanner' | 'card-terminal' | 'customer-display';
  label: string;
  /**
   * `internal` — sealed into the unit, never a separate id on the bus.
   * `detachable` — on a cable a person can remove, so absence is a FAULT.
   * `optional` — may or may not have been ordered; absence is not a fault.
   */
  attach: 'internal' | 'detachable' | 'optional';
  /** `vvvv:pppp`, lowercase, as the installer's probe emits them. */
  usbIds: string[];
  notes?: string;
}

/**
 * KNOWN VENDORS BY FUNCTION.
 *
 * Deliberately not exhaustive and never treated as if it were — anything not
 * here still resolves to `substituted` rather than `missing`, so a vendor we
 * have never met costs a softer word and not a false alarm. Adding a row here
 * only ever upgrades a guess to a certainty.
 *
 * ⚠️ The RUNTIME side does not need this list at all and must not grow one.
 * On the device, `kindByInterface()` reads the USB interface descriptor —
 * class 7 is a printer, class 3 protocol 1 is a boot keyboard, i.e. a wedge
 * scanner — which identifies ANY vendor's hardware correctly. This list exists
 * only because the installer captures `lsusb` ids with no descriptor detail,
 * so super-admin has nothing better to reason from.
 */
const PRINTER_VENDORS = [
  '4b43', '0fe6', '0416', // the units these tills ship with
  '04b8',                 // Epson
  '0519',                 // Star Micronics
  '1504',                 // Bixolon
  '0dd4',                 // Custom / Citizen
  '154f',                 // SNBC
];

const SCANNER_VENDORS = [
  '28e9',                 // the unit these tills ship with
  '05e0',                 // Symbol / Zebra
  '0c2e',                 // Honeywell / Metrologic
  '05f9',                 // Datalogic
  '1eab',                 // Newland
  '2dd6',                 // Netum and the no-name units
];

export const DEVICE_PROFILES: DeviceProfile[] = [
  {
    id: 'k10',
    buildTargets: ['android_dual_screen', 'android_single_screen'],
    displayName: 'K10 (Rockchip RK3566)',
    match: 'rk3568_r',
    // Plain adb-over-TCP on the standard port: an IP alone is a full address.
    transport: { kind: 'adb-tcp', defaultPort: 5555, needsPairing: false, usb: true },
    bootBrandingOverAdb: true,
    /*
      SEALED. The K10 is an all-in-one: its printer is inside the case and its
      card terminal rides the POS-Base. Neither is something an installer can
      forget to plug in, which is exactly why neither is marked `detachable` —
      flagging a missing printer id here would put a permanent red mark on every
      K10 that ever shipped.
    */
    peripherals: [
      { kind: 'printer', label: 'Built-in thermal printer', attach: 'internal', usbIds: ['0fe6:811e'],
        notes: 'Inside the case. It does not enumerate as a separate box, so absence on the bus is normal. An EXTERNAL printer of any make can also be cabled to this unit in addition to it.' },
      { kind: 'card-terminal', label: 'Card terminal via POS-Base', attach: 'detachable', usbIds: ['0e8d:2012'] },
      // A K10 ships without one but will take any USB scanner. `optional` so a
      // unit correctly built without one never reads as faulty.
      { kind: 'scanner', label: 'Barcode scanner', attach: 'optional', usbIds: [],
        notes: 'Not part of the K10 bundle, but any USB wedge scanner can be plugged in and will work.' },
    ],
  },
  {
    id: 'a10',
    buildTargets: ['android_dual_screen', 'android_single_screen'],
    displayName: 'A10 (Rockchip RK3588)',
    // Measured on the bench unit 2026-09-23, not taken from a catalog row.
    match: 'rk3588_u',
    /*
      WIRELESS DEBUGGING WITH PAIRING, like the Vector — but USB works too,
      unlike the Vector.

      The connect port is random per device and rotates: 37585 on the bench unit
      stopped answering within the hour while the mDNS name went on working, so
      an IP alone is not an address here. The first connection needs a 6-digit
      code shown on a SECOND random port, advertised only while the pairing
      dialog is open.

      usb: true is measured, not assumed — /sys/class/udc/ holds fc000000.usb
      and sys.usb.config is `adb`, so this board has a USB device controller and
      a cable is a real route. The Vector has neither, which is why it is false
      there.
    */
    transport: { kind: 'wireless-debugging', needsPairing: true, usb: true },
    // NOT YET VERIFIED on this hardware — it is a rooted Rockchip AOSP build so
    // an adb push is expected to work, and `true` is what unrecognised hardware
    // already defaults to, so this promises nothing new. Prove it on the first
    // unit that brands, and add a note here either way.
    bootBrandingOverAdb: true,
    /*
      🔴 THE A10 IS NOT A SEALED K10, AND THIS IS THE DIFFERENCE THAT MATTERS.

      Measured on the bench unit 2026-09-24, straight off /sys/bus/usb/devices:

        5-1     1a40:0201  USB 2.0 Hub [MTT]       ← internal
        ├ 5-1.2 0e8d:2012  POS-Base                ← card terminal
        ├ 5-1.3 222a:0001  ILITEK-TP               ← customer panel
        └ 5-1.7 214b:7250  USB2.0 HUB              ← DETACHABLE
          ├ 5-1.7.1 0fe6:811e  Virtual PRN         ← printer
          └ 5-1.7.2 28e9:0383  Barcode Scanner

      The printer and the scanner are BOTH downstream of one removable hub, so
      they arrive and disappear together. That is why they are `detachable` here
      and `internal` on the K10 despite the printer sharing an id — the id says
      what the device is, the profile says whether it can go missing.

      The customer panel is ILITEK and the cashier panel is eGalax. Do not swap
      them: they have been bound to the wrong display before and the unit came
      up dead to touch.
    */
    peripherals: [
      { kind: 'printer', label: 'Thermal printer (on the removable hub)', attach: 'detachable', usbIds: ['0fe6:811e'],
        notes: 'Shares hub 214b:7250 with the scanner — if both are missing it is the hub cable, not the devices.' },
      { kind: 'scanner', label: 'Barcode scanner (on the removable hub)', attach: 'detachable', usbIds: ['28e9:0383'],
        notes: 'A HID keyboard. It may not appear in UsbManager.getDeviceList() at all; the POS reads the input list too.' },
      { kind: 'card-terminal', label: 'Card terminal via POS-Base', attach: 'detachable', usbIds: ['0e8d:2012'],
        notes: 'Enumerates as soon as it has power, but the terminal behind it needs about a minute to answer SPIn. A reader that is not green yet is probably still booting.' },
      { kind: 'customer-display', label: 'Customer touch panel (ILITEK)', attach: 'internal', usbIds: ['222a:0001'],
        notes: 'ILITEK is the CUSTOMER panel; eGalax 0eef:c002 is the cashier panel.' },
    ],
  },
  {
    id: 'vector',
    buildTargets: ['android_dual_screen', 'android_single_screen'],
    displayName: 'Touch Dynamic Vector (MediaTek MT8395)',
    match: 'Vector',
    /*
      WIRELESS DEBUGGING, and an IP alone is NOT enough. The port is random per
      device and survives reboots only until it is toggled (33071 on our bench
      unit, 33515 on another), and the first connection needs a 6-digit pairing
      code shown on a SECOND random port that is advertised only while the
      pairing dialog is open. USB never enumerates on this unit at all.
    */
    transport: { kind: 'wireless-debugging', needsPairing: true, usb: false },
    // erofs system/vendor, read-only, no root: the splash lives in MediaTek
    // logo_a/logo_b and is reachable only by SP Flash Tool with a vendor image.
    bootBrandingOverAdb: false,
    notes: 'Boot branding needs a vendor image flashed with SP Flash Tool — it cannot be pushed over adb.',
    /*
      Everything on a Vector is on a cable — it has a rear I/O strip and no
      sealed peripherals at all. The printer is the same RONGTA id the A10 uses
      but reached directly rather than through a hub.

      The scanner is `optional` rather than `detachable` on purpose: Vectors ship
      both with and without one, so calling its absence a fault would put a red
      mark on correctly-built units. The A10 ships as one bundle, which is what
      lets it be `detachable` there.
    */
    peripherals: [
      { kind: 'printer', label: 'External thermal printer', attach: 'detachable', usbIds: ['0fe6:811e'],
        notes: 'USB-A only. The RJ12 socket beside the USB ports drives a cash drawer and will not run a printer.' },
      { kind: 'card-terminal', label: 'Card terminal (direct cable or POS-Base)', attach: 'detachable', usbIds: ['0e8d:2012'] },
      { kind: 'scanner', label: 'Barcode scanner', attach: 'optional', usbIds: [],
        notes: 'A HID wedge scanner of any make. It types barcodes and never appears on the USB device list.' },
    ],
  },
  {
    id: 'sunmi_t3',
    buildTargets: ['android_sunmi_t3'],
    displayName: 'SUNMI T3 + 80mm Printer',
    // Measured on the bench unit 2026-10-14 (serial TV0725AW40290, Android 13).
    // Covers both catalog builds — Single Screen and Dual Screen with NFC —
    // which report the same model string.
    match: 'T3',
    /*
      Wireless debugging with pairing; the connect port came from mDNS
      (_adb-tls-connect), not from the pairing port.

      DEVICE OWNER IS SUNMI's, NOT OURS. The unit ships enrolled in Sunmi Remote
      Control Pro (com.sunmi.remotecontrol.pro), so the installer must run with
      --allow-unmanaged and must never --claim-owner.
    */
    transport: { kind: 'wireless-debugging', needsPairing: true, usb: false },
    // User build, no adb root: the boot animation cannot be written.
    bootBrandingOverAdb: false,
    notes: 'Production Sunmi user build — no adb root, so boot branding cannot be pushed over adb.',
    peripherals: [
      { kind: 'printer', label: 'Built-in thermal printer (Sunmi print service)', attach: 'internal', usbIds: [],
        notes: 'Driven through woyou.aidlservice.jiuiv5, not USB — it never appears on the bus.' },
      { kind: 'customer-display', label: 'Detachable customer display', attach: 'optional', usbIds: [],
        notes: 'A Sunmi USB display presented by com.sunmi.usbscreen as a VIRTUAL presentation display (1280x800). Absent on the Single Screen build.' },
      { kind: 'scanner', label: 'External barcode scanner', attach: 'optional', usbIds: [],
        notes: 'A HID wedge scanner; the bench unit has a Newland NS021. It types barcodes and never appears on the USB device list.' },
    ],
  },
  {
    id: 'sunmi_t3_pro',
    buildTargets: ['android_sunmi_t3'],
    displayName: 'SUNMI T3 Pro (no printer)',
    // Measured on the bench unit 2026-10-29 (serial TK02251C40140, product
    // T3_PRO_E_GL, Android 13 user build). A DIFFERENT machine from the T3 80mm:
    // same family, but `T3 PRO` not `T3`, and no built-in printer. NFC (PN557) built in.
    // The MACHINE only: single/dual is the catalog model's variant, and the CPad Pay
    // is a card reader picked per register — neither belongs to the hardware.
    match: 'T3 PRO',
    // Wireless debugging with pairing, same as the T3 80mm (connect 34207 ≠ pair 46881 on the bench unit).
    transport: { kind: 'wireless-debugging', needsPairing: true, usb: false },
    bootBrandingOverAdb: false,
    notes: 'Production Sunmi user build — no adb root, so boot branding cannot be pushed over adb.',
    peripherals: [
      { kind: 'printer', label: 'External receipt printer', attach: 'optional', usbIds: [],
        notes: 'The T3 Pro has NO built-in printer — never the Sunmi print service. Any USB printer is driven like the other tills.' },
      { kind: 'customer-display', label: 'SUNMI USB customer display (e.g. CPad Pay)', attach: 'optional', usbIds: [],
        notes: 'Presented by com.sunmi.usbscreen as a VIRTUAL 1280x800 presentation display. Absent on a single-screen setup. A CPad Pay is also a card reader — chosen in the register card setup.' },
      { kind: 'scanner', label: 'Barcode scanner', attach: 'optional', usbIds: [],
        notes: 'SUNMI "SM-2D DEFHID+KBW" — a HID wedge scanner; types barcodes.' },
    ],
  },
  {
    id: 'sunmi_d3_pro',
    buildTargets: ['android_sunmi_t3'],
    displayName: 'SUNMI D3 Pro',
    // Measured on the bench unit 2026-10-31 (serial DP0225CS11956, product D3_PRO,
    // Android 13 user build, 1920x1080). Same build as the T3 family — the till tells
    // the machines apart by Build.MODEL. No Tapro on board; NO built-in printer (owner 2026-10-31).
    // The CPad on its USB port is a card reader + customer screen picked per register.
    match: 'D3 PRO',
    // Wireless debugging with pairing; the connect port comes from mDNS (_adb-tls-connect).
    transport: { kind: 'wireless-debugging', needsPairing: true, usb: false },
    bootBrandingOverAdb: false,
    notes: 'Production Sunmi user build — no adb root, so boot branding cannot be pushed over adb.',
    peripherals: [
      { kind: 'printer', label: 'External receipt printer', attach: 'optional', usbIds: [],
        notes: 'The D3 Pro has NO built-in printer — never the Sunmi print service. Any USB printer is driven like the other tills.' },
      { kind: 'customer-display', label: 'SUNMI CPad (via RubyTill Pay)', attach: 'optional', usbIds: [],
        notes: 'The CPad attaches as its own Android device (RNDIS + adb gadget), NOT a usbscreen presentation display. It is a certified payment device — no adb; RubyTill Pay + Tapro arrive through the SUNMI store / MDM.' },
      { kind: 'scanner', label: 'Barcode scanner', attach: 'optional', usbIds: [],
        notes: 'A HID wedge scanner; types barcodes.' },
    ],
  },
];

/**
 * Expected against seen, for one unit.
 *
 * `seen` is whatever `DeviceFacts.usbPeripherals` captured — bare `vvvv:pppp`
 * ids or whole lsusb lines, since older installs stored the latter. Matching is
 * done on the id found ANYWHERE in the string so both shapes work.
 *
 * `internal` and `optional` peripherals can never be `missing`: a sealed printer
 * does not enumerate and an unordered scanner was never coming. Only a
 * `detachable` one that is absent is a fault, which is the whole point of the
 * three-way split.
 */
export interface PeripheralCheck extends PeripheralExpectation {
  /**
   * `present`        — the expected id, or a known vendor of this kind, is there.
   * `substituted`    — not found, but the bus carries a device we cannot account
   *                    for, so a third-party unit is the likely explanation.
   *                    NOT a fault: say so softly and let a human look.
   * `missing`        — not found, and every id on the bus is accounted for.
   * `not-applicable` — internal or optional; absence means nothing.
   */
  status: 'present' | 'substituted' | 'missing' | 'not-applicable';
  /** The unrecognised ids that might be this peripheral, for `substituted`. */
  candidates?: string[];
}

/** Ids that are never a peripheral: root hubs, plain hubs, the board itself. */
const INFRASTRUCTURE_VENDORS = ['1d6b', '1a40', '214b', '2109', '05e3'];

function vendorsFor(kind: PeripheralExpectation['kind']): string[] {
  if (kind === 'printer') return PRINTER_VENDORS;
  if (kind === 'scanner') return SCANNER_VENDORS;
  return [];
}

export function checkPeripherals(
  p: DeviceProfile | null,
  seen: string[] | undefined,
): PeripheralCheck[] {
  if (!p) return [];
  const ids = (seen || [])
    .map((s) => s.match(/([0-9a-f]{4}):([0-9a-f]{4})/i))
    .filter(Boolean)
    .map((m) => `${m![1].toLowerCase()}:${m![2].toLowerCase()}`);

  /*
    WHAT IS ON THE BUS THAT NO EXPECTATION CLAIMS.

    Computed once over the WHOLE profile rather than per peripheral, because a
    device claimed by the terminal row must not also be offered as a candidate
    printer. Hubs and root hubs are struck out too — a hub is how a peripheral
    arrives, never the peripheral.
  */
  const claimed = new Set<string>();
  for (const x of p.peripherals) {
    for (const id of x.usbIds) if (ids.includes(id.toLowerCase())) claimed.add(id.toLowerCase());
    for (const id of ids) {
      if (vendorsFor(x.kind).includes(id.slice(0, 4))) claimed.add(id);
    }
  }
  const unaccounted = ids.filter(
    (id) => !claimed.has(id) && !INFRASTRUCTURE_VENDORS.includes(id.slice(0, 4)),
  );

  return p.peripherals.map((x): PeripheralCheck => {
    if (x.usbIds.some((id) => ids.includes(id.toLowerCase()))) {
      return { ...x, status: 'present' };
    }
    // A different model from a vendor that makes this kind of device IS this
    // kind of device. Both tills take third-party printers and scanners, so
    // this is an ordinary outcome, not an exception.
    if (ids.some((id) => vendorsFor(x.kind).includes(id.slice(0, 4)))) {
      return { ...x, status: 'present' };
    }
    // Absence only means something for a peripheral that is supposed to be
    // cabled. Reporting "missing" for a sealed printer would be a false alarm
    // on every K10 ever installed.
    if (x.attach !== 'detachable') return { ...x, status: 'not-applicable' };
    /*
      🔴 THE LAST GATE BEFORE A FALSE ALARM. Something is plugged in that we
      cannot name, and the thing we were looking for is not here — on hardware
      that explicitly supports third-party peripherals, the overwhelmingly
      likely reading is that this IS it, under a vendor id we do not know.
      Saying "missing" would send someone to re-seat a working cable.
    */
    if (unaccounted.length) return { ...x, status: 'substituted', candidates: unaccounted };
    return { ...x, status: 'missing' };
  });
}

/**
 * The profiles to offer for a build. No build chosen yet ⇒ all of them. The
 * row's CURRENT profile is always kept, even under another build, so nothing
 * already entered silently disappears from the picker.
 */
export function profilesForBuild(buildTarget?: string | null, currentId?: string | null): DeviceProfile[] {
  if (!buildTarget) return [...DEVICE_PROFILES];
  return DEVICE_PROFILES.filter((p) => p.buildTargets.includes(buildTarget) || p.id === currentId);
}

/** The profile a unit's reported model matches, if we know it. */
export function profileForModel(model?: string | null): DeviceProfile | null {
  if (!model) return null;
  return DEVICE_PROFILES.find((p) => p.match === model) || null;
}

export function profileById(id?: string | null): DeviceProfile | null {
  if (!id) return null;
  return DEVICE_PROFILES.find((p) => p.id === id) || null;
}

/**
 * The install panel's profile choice for a unit, from its catalog model's
 * `appProfile.installProfileId`: that id when this panel knows it, else null
 * (the operator picks). Derived fresh per unit — never the previous unit's pick.
 */
export function catalogInstallProfileId(installProfileId?: string | null): string | null {
  return installProfileId && profileById(installProfileId) ? installProfileId : null;
}

/**
 * What the connection step asks for on this hardware.
 *
 * Each field is its own answer, because they are three different things read
 * off three different places on the device: the connect port is not the
 * pairing port, and neither is the code. One combined box made the operator
 * assemble a string, and assembling it wrongly failed with an adb timeout
 * rather than a sentence.
 */
export interface ConnectionFields {
  /** Is a port required, or merely assumed? */
  portRequired: boolean;
  portPlaceholder: string;
  /** Does this hardware use the Wireless-debugging pairing handshake at all? */
  pairing: boolean;
  help: string;
}

export function connectionFields(p: DeviceProfile | null): ConnectionFields | null {
  if (!p) return null;
  if (p.transport.kind === 'wireless-debugging') {
    return {
      portRequired: true,
      portPlaceholder: '37251',
      pairing: true,
      // Wireless debugging and a USB route are not mutually exclusive: the A10
      // needs the pairing dance over the network AND enumerates over a cable.
      // Saying "leave the IP blank for USB" on a Vector would be a lie, so the
      // sentence is added only where the profile says a cable works.
      help: 'Read the IP and port from Developer options → Wireless debugging on the device. '
        + 'The port is random per device and changes when debugging is toggled, so an IP alone will not connect.'
        + (p.transport.usb ? ' Leave the IP blank to use a USB cable instead.' : ''),
    };
  }
  return {
    portRequired: false,
    portPlaceholder: String(p.transport.defaultPort ?? ''),
    pairing: false,
    help: p.transport.usb
      ? `Port ${p.transport.defaultPort} is assumed if you leave it blank. Leave the IP blank to use a USB cable.`
      : `Port ${p.transport.defaultPort} is assumed if you leave it blank.`,
  };
}

/**
 * The single address argv gets: `<ip>` or `<ip>:<port>`. Empty means "whatever
 * is already connected", which is the USB case.
 */
export function composeAddress(ip: string, port: string): string {
  const a = ip.trim();
  const p = port.trim();
  if (!a) return '';
  return p ? `${a}:${p}` : a;
}

/**
 * The reason this run cannot start, or null.
 *
 * 🔴 THE PROFILE IS REQUIRED. REVERSED 2026-09-24 BY THE PRODUCT OWNER.
 *
 * This function used to open with a documented decision titled "NO PROFILE IS
 * NOT AN ERROR", on the reasoning that "install live on one click" was an
 * invariant of the panel and a profile was a second click in front of every
 * operator. That reasoning no longer holds and the decision is withdrawn:
 *
 *   An install that is not tied to a hardware profile cannot be checked for
 *   Device Owner, and cannot be checked for the right peripherals. Device
 *   Owner can only be claimed on a factory-fresh device and can only be
 *   removed by another factory reset — so a unit installed without it can
 *   never be remotely restarted, locked down or wiped, and cannot be enrolled
 *   later without erasing it. This panel is the last place that mistake can
 *   still be caught, and it cannot catch it without knowing the hardware.
 *
 * So a missing profile is now the FIRST error returned, and the message names
 * the action rather than the rule. Every other check below is unchanged.
 */
export function validateConnection(
  p: DeviceProfile | null,
  v: { ip: string; port: string; pairPort: string; pairCode: string },
): string | null {
  if (!p) {
    return 'Choose the device profile for this hardware before installing — '
      + 'it is what tells us how this unit connects, which peripherals it should have, '
      + 'and whether Device Owner is set.';
  }
  const f = connectionFields(p)!;
  if (f.portRequired && v.ip.trim() && !v.port.trim()) {
    return `${p.displayName} connects over Wireless debugging — it needs the port as well as the IP.`;
  }
  if (!p.transport.usb && !v.ip.trim()) {
    return `${p.displayName} has no USB route, so an IP address is required.`;
  }
  // Pairing is optional (an already-paired unit skips it), but half of it is
  // never enough: a port with no code cannot pair, and a code with no port has
  // nowhere to go.
  const hasPort = !!v.pairPort.trim();
  const hasCode = !!v.pairCode.trim();
  if (hasPort !== hasCode) return 'Pairing needs both the pairing port and the 6-digit code.';
  if (hasCode && !/^\d{6}$/.test(v.pairCode.trim())) return 'The pairing code is 6 digits.';
  return null;
}
